import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import LanguagePickerButton from '@/components/LanguagePickerButton';
import AppHeader from '@/components/AppHeader';
import Recaptcha, { RECAPTCHA_ENABLED } from '@/components/Recaptcha';
import { extractErrorMessage } from '@/lib/errors';
import { ArrowLeft, Loader2 } from 'lucide-react';

// Minimal country-code list — the dialer picker matches the one on
// Login so a user who typed their number in once doesn't have to
// relearn the format here.
const COUNTRY_CODES = [
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenya' },
  { code: '+233', country: 'Ghana' },
  { code: '+27',  country: 'South Africa' },
  { code: '+256', country: 'Uganda' },
  { code: '+255', country: 'Tanzania' },
  { code: '+251', country: 'Ethiopia' },
  { code: '+20',  country: 'Egypt' },
  { code: '+1',   country: 'Canada/USA' },
  { code: '+44',  country: 'UK' },
  { code: '+91',  country: 'India' },
  { code: '+86',  country: 'China' },
].sort((a, b) => a.country.localeCompare(b.country));

/**
 * Step 1 of password reset: user types their phone, we POST to
 * `/auth/password-reset/request`. The backend returns the SAME
 * generic 200 whether or not the phone is registered (anti-
 * enumeration), so the UI always advances to the OTP page on
 * success and stashes the phone in location state for step 2.
 */
export default function ResetPasswordRequestPage() {
  const [countryCode, setCountryCode] = useState('+234');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim() || phoneNumber.length < 7) {
      toast({ title: t('auth.login.errors.invalidPhone'), variant: 'destructive' });
      return;
    }
    if (RECAPTCHA_ENABLED && !captchaToken) {
      toast({ title: t('auth.login.errors.captcha'), variant: 'destructive' });
      return;
    }

    const fullPhone = `${countryCode}${phoneNumber.replace(/^0+/, '')}`;
    setLoading(true);
    try {
      await authAPI.requestPasswordReset({
        phone: fullPhone,
        recaptcha_token: captchaToken,
      });
      // Always advance to the OTP page on 200 — the backend treats
      // "phone unknown" the same as "phone known" so the user
      // experience must too, otherwise the response code becomes a
      // user-enumeration oracle.
      navigate('/forgot-password/verify', { state: { phone: fullPhone } });
    } catch (error) {
      recaptchaRef.current?.reset();
      // 429 is the one case we must distinguish — the user needs to
      // know to wait, not that they typed something wrong.
      if (error?.response?.status === 429) {
        toast({
          title: t('auth.resetRequest.errors.rateLimited', {
            defaultValue: 'Too many attempts. Please wait a few minutes and try again.',
          }),
          variant: 'destructive',
        });
      } else if (error?.response?.status === 503) {
        // Reset by SMS is off (no SMS provider yet): point to support.
        toast({ title: t('auth.resetRequest.errors.unavailable'), variant: 'destructive' });
      } else {
        toast({
          title: extractErrorMessage(error, t('auth.resetRequest.errors.failed', {
            defaultValue: 'Could not start a reset. Please try again.',
          })),
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader trailing={<LanguagePickerButton />} />

      <div className="flex-1 flex flex-col p-6">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-sm text-gray-600 mb-4 hover:text-gray-900"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" /> {t('auth.resetRequest.backToLogin', { defaultValue: 'Back to sign in' })}
        </button>

        <div className="text-center mt-4 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('auth.resetRequest.title', { defaultValue: 'Forgot your password?' })}
          </h1>
          <p className="text-gray-600">
            {t('auth.resetRequest.subtitle', {
              defaultValue: "We'll send a 6-digit code to your phone so you can set a new password.",
            })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1">
          <label htmlFor="reset-phone" className="block text-sm font-semibold text-gray-900 mb-2">
            {t('auth.login.phoneLabel')}
          </label>
          <div className="flex gap-2 mb-6">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger className="w-[130px] h-14 bg-white border-gray-200" data-testid="reset-country-code">
                <SelectValue>
                  <span>{countryCode}</span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {COUNTRY_CODES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="reset-phone"
              type="tel"
              placeholder={t('auth.login.phonePlaceholder')}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
              className="flex-1 h-14 bg-white border-gray-200 text-lg"
              data-testid="reset-phone-input"
            />
          </div>

          <Recaptcha ref={recaptchaRef} onChange={setCaptchaToken} />

          <Button
            type="submit"
            disabled={loading || (RECAPTCHA_ENABLED && !captchaToken)}
            className="w-full h-14 bg-farm-primary hover:bg-farm-primary-dark text-white font-semibold text-lg rounded-xl mb-4"
            data-testid="reset-send-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t('auth.resetRequest.submit', { defaultValue: 'Send reset code' })
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
